/**
 * AfterLessonReflectionCard — calm free-text + voice reflection slot.
 *
 * Phase 2 of the Contextual Guidance & Reflection System.
 * May 14, 2026 — UX direction change (operator feedback):
 *   Removed binary/quiz-style chip options. The learner now writes or
 *   dictates a short natural response. The LLM interprets it and
 *   replies with ONE calm 1–3 sentence supportive paragraph.
 *
 * States:
 *   • silent  → /me returned {prompt: null}
 *   • prompt  → question + textarea + microphone + skip
 *   • thinking → LLM call in flight
 *   • reply   → supportive paragraph + close X
 */
import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import SupportPercentChoice from "./SupportPercentChoice";
import { Mic, MicOff, Send, X, Loader2, Sparkles } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const MIN_LEARNER_CHARS = 2;
const MAX_LEARNER_CHARS = 1000;

// Lazily detect Web Speech API support (Chrome, Edge, Safari iOS 14+).
const getSpeechRecognition = () => {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
};

export default function AfterLessonReflectionCard() {
  const [prompt, setPrompt] = useState(null);
  const [supportReceived, setSupportReceived] = useState(null);
  const [supportAnswered, setSupportAnswered] = useState(false);
  const [reflectedFocus, setReflectedFocus] = useState("");
  const [text, setText] = useState("");
  const [replyText, setReplyText] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [recording, setRecording] = useState(false);
  const [error, setError] = useState(null);
  const recognitionRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/api/after-lesson-reflection/me`,
        { withCredentials: true },
      );
      setPrompt(data?.prompt || null);
      setText("");
      setSupportReceived(data?.prompt?.support_received_percent ?? null);
      setSupportAnswered(Boolean(data?.prompt?.support_answered_at));
      setReplyText(null);
    } catch {
      setPrompt(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Stop any in-flight voice recognition when the card unmounts or the
  // prompt clears.
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch { /* no-op */ }
        recognitionRef.current = null;
      }
    };
  }, []);

  const startVoice = () => {
    const SR = getSpeechRecognition();
    if (!SR || submitting || recording) return;
    try {
      const r = new SR();
      r.continuous = true;
      r.interimResults = true;
      r.lang = "en-GB";
      let accumulated = text;
      r.onresult = (event) => {
        let interim = "";
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            accumulated = (accumulated + " " + transcript).trim();
          } else {
            interim += transcript;
          }
        }
        const draft = (accumulated + " " + interim).slice(0, MAX_LEARNER_CHARS).trimStart();
        setText(draft);
      };
      r.onend = () => setRecording(false);
      r.onerror = () => setRecording(false);
      recognitionRef.current = r;
      r.start();
      setRecording(true);
    } catch {
      setRecording(false);
    }
  };

  const stopVoice = () => {
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch { /* no-op */ }
      recognitionRef.current = null;
    }
    setRecording(false);
  };

  const onSubmit = async () => {
    if (!prompt) return;
    const learnerText = text.trim();
    if (learnerText.length < MIN_LEARNER_CHARS) return;
    stopVoice();
    setSubmitting(true);
    setError(null);
    try {
      const { data } = await axios.post(
        `${API}/api/after-lesson-reflection/respond`,
        {
          signal_key: prompt.signal_key,
          trigger:    prompt.trigger,
          lesson_id:  prompt.lesson_id || null,
          learner_text: learnerText.slice(0, MAX_LEARNER_CHARS),
          focus: prompt.focus || null,
          support_received_percent: prompt.trigger === "lesson_just_completed" ? supportReceived : null,
        },
        { withCredentials: true },
      );
      setReplyText(data?.follow_up?.body || "Saved.");
      setReflectedFocus(data?.focus || prompt.focus || "your lesson goal");
    } catch {
      setError("Your reflection was not saved. Please try again — your words are still here.");
    } finally {
      setSubmitting(false);
    }
  };

  const onSkip = async () => {
    if (!prompt || submitting) return;
    stopVoice();
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/api/after-lesson-reflection/dismiss`,
        { signal_key: prompt.signal_key, trigger: prompt.trigger },
        { withCredentials: true },
      );
      setPrompt(null);
      setReplyText(null);
    } catch {
      setPrompt(null);
    } finally {
      setSubmitting(false);
    }
  };

  const onCloseReply = () => {
    setPrompt(null);
    setReplyText(null);
  };

  // Silent state — the slot earns visual weight only when there's
  // something to say.
  if (loading) return null;
  if (!prompt) return null;

  // ── State 2: LLM reply displayed ──────────────────────────────────
  if (replyText) {
    return (
      <section
        data-testid="after-lesson-reflection-followup"
        data-trigger={prompt.trigger}
        className="relative rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 sm:p-5 shadow-sm"
      >
        <button
          type="button"
          data-testid="after-lesson-reflection-close"
          onClick={onCloseReply}
          aria-label="Close"
          className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="pr-7 flex items-start gap-3">
          <div className="w-9 h-9 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center flex-none">
            <Sparkles className="w-4 h-4 text-emerald-600 dark:text-emerald-300" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 dark:text-slate-400">
              A short reply
            </p>
            <p
              data-testid="after-lesson-reflection-followup-body"
              className="text-[14px] text-slate-700 dark:text-slate-200 mt-1.5 leading-relaxed"
            >
              {replyText}
            </p>
            {prompt.trigger === "lesson_just_completed" && <>
              <p className="mt-2 text-sm">Support you felt you received: {supportReceived == null ? "not stated" : `${supportReceived}%`}. This is your reflection, separate from learning progress.</p>
              <a className="block min-h-11 mt-3 underline" href={`/learn/learning?question=${encodeURIComponent(`Help me understand ${reflectedFocus}. Ask me one question at a time about my understanding, including why relevant mirror or safety checks matter. Do not assume I have done them.`)}`}>Explore my understanding with Karen</a>
            </>}
          </div>
        </div>
      </section>
    );
  }

  if (prompt.trigger === "lesson_just_completed" && !supportAnswered) {
    return <section className="rounded-2xl border p-5 space-y-3" data-testid="after-lesson-support">
      <SupportPercentChoice value={supportReceived} onChange={setSupportReceived} allowNone label="How much support do you feel you had during this lesson?" />
      {prompt.support_requested_percent != null && <p>You requested {prompt.support_requested_percent}% support beforehand. Your answer can be different.</p>}
      <button type="button" className="min-h-11 underline" onClick={() => setSupportAnswered(true)}>Continue to reflection</button>
      <button type="button" className="min-h-11 underline ml-4" onClick={onSkip} disabled={submitting}>Skip for now</button>
    </section>;
  }

  // ── State 1: prompt + free text + voice ──────────────────────────
  const SR = getSpeechRecognition();
  const canSubmit = text.trim().length >= MIN_LEARNER_CHARS && !submitting;

  return (
    <section
      data-testid="after-lesson-reflection-card"
      data-trigger={prompt.trigger}
      data-signal-key={prompt.signal_key}
      className="relative rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 sm:p-5 shadow-sm"
    >
      <div>
        <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 dark:text-slate-400">
          A quiet moment
        </p>
        <h3
          data-testid="after-lesson-reflection-question"
          className="text-base sm:text-lg font-extrabold text-slate-900 dark:text-white leading-tight mt-1"
        >
          {prompt.question}
        </h3>

        <textarea
          data-testid="after-lesson-reflection-text"
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, MAX_LEARNER_CHARS))}
          placeholder={prompt.placeholder || "A sentence or two in your own words…"}
          disabled={submitting}
          rows={3}
          className="mt-3 w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2.5 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-slate-300 dark:focus:ring-slate-600 resize-none"
        />
        <div className="mt-1 flex items-center justify-between">
          <p className="text-[10px] text-slate-400">
            {recording
              ? "Listening… tap mic to stop"
              : `${text.length} / ${MAX_LEARNER_CHARS}`}
          </p>
        </div>

        {error && (
          <p
            role="alert"
            data-testid="after-lesson-reflection-error"
            className="mt-2 text-xs font-semibold text-rose-600 dark:text-rose-300"
          >
            {error}
          </p>
        )}

        <div className="mt-3 flex items-center gap-2">
          <button
            type="button"
            data-testid="after-lesson-reflection-submit"
            onClick={onSubmit}
            disabled={!canSubmit}
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-xs font-extrabold hover:bg-slate-800 dark:hover:bg-slate-100 active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {submitting
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <Send className="w-3.5 h-3.5" />}
            {submitting ? "Sending…" : "Send"}
          </button>
          {SR && (
            <button
              type="button"
              data-testid="after-lesson-reflection-mic"
              onClick={recording ? stopVoice : startVoice}
              disabled={submitting}
              aria-label={recording ? "Stop voice input" : "Use voice"}
              aria-pressed={recording}
              className={`inline-flex items-center justify-center h-9 w-9 rounded-xl border ${
                recording
                  ? "bg-rose-50 dark:bg-rose-900/20 border-rose-300 text-rose-600 animate-pulse"
                  : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-slate-300 dark:hover:border-slate-600"
              } disabled:opacity-50 disabled:cursor-not-allowed transition`}
            >
              {recording ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            </button>
          )}
          <button
            type="button"
            data-testid="after-lesson-reflection-skip"
            onClick={onSkip}
            disabled={submitting}
            className="ml-auto inline-flex items-center h-9 px-3 rounded-xl text-xs font-bold text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 disabled:opacity-50 transition-colors"
          >
            Skip for now
          </button>
        </div>
      </div>
    </section>
  );
}

